// One page size, named once, because the server validates it against the same
// number: DashboardPageLimit in functions/src/dashboard-query.ts accepts only
// 20/25/100/300/600, and ROLE_DASHBOARD_SCAN_LIMIT is what the scan actually
// takes. Raising this without raising those returns a smaller page silently.
//
// 600 holds the whole 525-container fleet in ONE request (2026-08-13). That
// matters more for correctness than for speed: the scan is ordered by document
// id, which has nothing to do with Ocean Status, so any partial fleet is an
// arbitrary slice. The table used to sort its first 300, paint a confident
// order, and then re-sort when the second page arrived - Alan's "ordering
// missing" reports have this shape. One page means the first paint is the right
// order. It degrades gracefully: past 600 containers the controller reports
// hasMore again and pages exactly as before.
export const DASHBOARD_PAGE_LIMIT = 600;

export function prefetchAnchorIndex(rowCount) {
  return rowCount > 0 ? Math.max(0, rowCount - 10) : -1;
}

export function createDashboardDataController({ callList, normalizeRow, onChange }) {
  if (typeof callList !== 'function' || typeof normalizeRow !== 'function'
    || typeof onChange !== 'function') {
    throw new TypeError('Dashboard controller dependencies are invalid.');
  }

  let generation = 0;
  let rows = [];
  let cursor = null;
  let hasMore = false;
  let loading = false;
  let query = {};
  let summary = null;
  let error = null;

  function state() {
    return { rows: [...rows], hasMore, loading, summary, error };
  }

  async function requestPage(limit, replace) {
    if (loading) return;
    loading = true;
    error = null;
    const token = generation;
    onChange(state());
    try {
      const response = await callList({ ...query, limit, cursor: replace ? null : cursor });
      if (token !== generation) return;
      const data = response?.data;
      if (!Array.isArray(data?.items) || typeof data.hasMore !== 'boolean') {
        throw new TypeError('DASHBOARD_RESPONSE_INVALID');
      }
      const nextCursor = typeof data.nextCursor === 'string' && data.nextCursor
        ? data.nextCursor : null;
      if (data.hasMore && (!nextCursor || nextCursor === cursor)) {
        throw new TypeError('DASHBOARD_PAGINATION_INVALID');
      }
      const nextRows = data.items.map(normalizeRow);
      rows = replace ? nextRows : [...rows, ...nextRows.filter((candidate) => (
        !rows.some((row) => row.id === candidate.id)
      ))];
      cursor = nextCursor;
      hasMore = data.hasMore;
      if (replace) summary = data.summary ?? null;
      loading = false;
      onChange(state());
    } catch (caught) {
      if (token === generation) {
        error = caught;
        loading = false;
        onChange(state());
      }
      throw caught;
    } finally {
      if (token === generation) loading = false;
    }
  }

  return {
    reset(nextQuery) {
      generation += 1;
      query = nextQuery && typeof nextQuery === 'object' ? { ...nextQuery } : {};
      rows = [];
      cursor = null;
      hasMore = false;
      loading = false;
      summary = null;
      error = null;
      return requestPage(DASHBOARD_PAGE_LIMIT, true);
    },
    // One page size for everything (Alan, 2026-08-11: "remove the opening 25
    // call"). The first request used to be 25 so the table painted something
    // immediately, which was right when a continuation was 20 rows - paint early,
    // then grind. It stopped being right once a continuation became 300: his
    // network tab showed the opening 25 costing 1.45s to show twenty-five rows
    // slightly sooner, while a full 300-row page landed in 934ms. A small page is
    // not a cheap page - most of that time is the round trip, not the rows - so
    // the opening 25 was buying a worse first paint AND an extra request.
    //
    // Three requests, then two, and since 2026-08-13 one - see the constant above.
    loadNext() {
      return hasMore ? requestPage(DASHBOARD_PAGE_LIMIT, false) : Promise.resolve();
    },
    refresh() {
      generation += 1;
      cursor = null;
      hasMore = false;
      loading = false;
      error = null;
      return requestPage(DASHBOARD_PAGE_LIMIT, true);
    },
    maybePrefetch(visibleEndIndex) {
      return hasMore && rows.length - visibleEndIndex <= 10
        ? requestPage(DASHBOARD_PAGE_LIMIT, false) : Promise.resolve();
    },
    patchRow(id, patch) {
      if (typeof id !== 'string' || !id || !patch || typeof patch !== 'object'
        || Array.isArray(patch)) return false;
      const index = rows.findIndex((row) => row.id === id);
      if (index < 0) return false;
      rows = rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row);
      onChange(state());
      return true;
    },
    async ensureRow({ id, search } = {}) {
      if (typeof id !== 'string' || !id || typeof search !== 'string' || !search.trim()) {
        return false;
      }
      if (rows.some((row) => row.id === id)) return true;
      const token = generation;
      const response = await callList({
        search: search.trim(),
        limit: 25,
        cursor: null,
      });
      if (token !== generation) return false;
      const data = response?.data;
      if (!Array.isArray(data?.items) || typeof data.hasMore !== 'boolean') {
        throw new TypeError('DASHBOARD_RESPONSE_INVALID');
      }
      const match = data.items.map(normalizeRow).find((row) => row.id === id);
      if (!match) return false;
      if (!rows.some((row) => row.id === id)) {
        rows = [...rows, match];
        onChange(state());
      }
      return true;
    },
    snapshot: state,
    destroy() {
      generation += 1;
      rows = [];
      cursor = null;
      hasMore = false;
      loading = false;
      summary = null;
      error = null;
    },
  };
}
